import React, { useState, useEffect } from "react";
import { Modal, Select, Descriptions, Table, Button, Alert, Spin, Tag, message } from "antd";
import { useNavigate } from "react-router-dom";
import GETJwtToken from "../../../redux/authentication/authentication";
import useGetAllOrganizationsLight from "../../../customHooks/GET_requests/useGetAllOrganizationsLight";
import { formatUuidT } from "../../../flatbuffers/display";
import {
  UpgradeAnalysis,
  UpgradeAnalysisItem,
  UpgradeExecutionResult,
  UpgradeExecutionItem,
} from "../../../interfaces/Import.interface";

interface OrganizationUpgradeModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

function OrganizationUpgradeModal({ open, onClose, onSuccess }: OrganizationUpgradeModalProps) {
  const navigate = useNavigate();
  const baseUrl = process.env.REACT_APP_LIFT_NET_CLOUD_API_ONLINE || "";

  const [step, setStep] = useState<"select" | "analysis" | "summary">("select");
  const [selectedOrgId, setSelectedOrgId] = useState<string>("");
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isExecuting, setIsExecuting] = useState(false);
  const [analysis, setAnalysis] = useState<UpgradeAnalysis | null>(null);
  const [executionResult, setExecutionResult] = useState<UpgradeExecutionResult | null>(null);
  const [confirmVisible, setConfirmVisible] = useState(false);

  const { organizationsLight, isLoadingGetAllOrganizationsLight } = useGetAllOrganizationsLight();

  const selectedOrgName = String(
    organizationsLight.find((o) => formatUuidT(o.id) === selectedOrgId)?.name ?? "",
  );

  const resetState = () => {
    setStep("select");
    setSelectedOrgId("");
    setIsAnalyzing(false);
    setIsExecuting(false);
    setAnalysis(null);
    setExecutionResult(null);
    setConfirmVisible(false);
  };

  useEffect(() => {
    if (!open) {
      resetState();
    }
  }, [open]);

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handleAnalyze = async () => {
    if (!selectedOrgId) {
      message.error("Please select an organization");
      return;
    }

    setIsAnalyzing(true);
    try {
      const token = await GETJwtToken();
      const res = await fetch(`${baseUrl}/import/upgrade-org/analyze`, {
        method: "POST",
        headers: {
          authorization: token,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ org_id: selectedOrgId }),
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => null);
        throw new Error(errData?.message || `Analysis failed (${res.status})`);
      }

      const result = await res.json();
      const analysisData: UpgradeAnalysis = result.data || result;
      setAnalysis(analysisData);
      setStep("analysis");
    } catch (err: any) {
      message.error(err?.message || "Analysis failed");
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleExecute = async () => {
    setConfirmVisible(false);
    setIsExecuting(true);
    try {
      const token = await GETJwtToken();
      const res = await fetch(`${baseUrl}/import/upgrade-org/execute`, {
        method: "POST",
        headers: {
          authorization: token,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ org_id: selectedOrgId }),
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => null);
        throw new Error(errData?.message || `Upgrade execution failed (${res.status})`);
      }

      const result = await res.json();
      const executionData: UpgradeExecutionResult = result.data || result;
      setExecutionResult(executionData);
      setStep("summary");
      onSuccess();
    } catch (err: any) {
      message.error(err?.message || "Upgrade execution failed");
    } finally {
      setIsExecuting(false);
    }
  };

  const analysisColumns = [
    {
      title: "Category",
      dataIndex: "label",
      key: "label",
    },
    {
      title: "Records to Upgrade",
      dataIndex: "count",
      key: "count",
      render: (val: number) => <Tag color={val > 0 ? "orange" : "green"}>{val}</Tag>,
    },
  ];

  const executionColumns = [
    {
      title: "Category",
      dataIndex: "label",
      key: "label",
    },
    {
      title: "Records Updated",
      dataIndex: "updated",
      key: "updated",
      render: (val: number) => <Tag color={val > 0 ? "blue" : "default"}>{val}</Tag>,
    },
  ];

  const renderSelectStep = () => (
    <div style={{ marginBottom: 16 }}>
      <div style={{ marginBottom: 8 }}>
        <span style={{ fontWeight: 500 }}>Organization to Upgrade</span>
      </div>
      <Select
        placeholder="Search and select an organization"
        value={selectedOrgId || undefined}
        onChange={(val: string) => {
          setSelectedOrgId(val);
          setAnalysis(null);
          setStep("select");
        }}
        style={{ width: "100%" }}
        showSearch
        optionFilterProp="children"
        loading={isLoadingGetAllOrganizationsLight}
        filterOption={(input, option) =>
          (option?.children as unknown as string)?.toLowerCase().includes(input.toLowerCase()) ??
          false
        }
      >
        {organizationsLight.map((org) => (
          <Select.Option key={formatUuidT(org.id)} value={formatUuidT(org.id)}>
            {String(org.name ?? "")}
          </Select.Option>
        ))}
      </Select>
    </div>
  );

  const renderAnalysisStep = () => {
    if (!analysis) return null;

    if (analysis.already_upgraded) {
      return (
        <Alert
          type="success"
          showIcon
          message="Already Up to Date"
          description={`"${
            analysis.org_name || selectedOrgName
          }" is already fully upgraded. No changes are needed.`}
          style={{ marginBottom: 16 }}
        />
      );
    }

    return (
      <>
        <Alert
          type="info"
          showIcon
          message="Upgrade Analysis"
          description={`Found ${analysis.total_records} record(s) across ${
            analysis.items?.length || 0
          } categories that need upgrading in "${analysis.org_name || selectedOrgName}".`}
          style={{ marginBottom: 16 }}
        />
        {analysis.items && analysis.items.length > 0 && (
          <Table
            dataSource={analysis.items}
            columns={analysisColumns}
            rowKey={(record: UpgradeAnalysisItem) => record.table}
            pagination={false}
            size="small"
            summary={() => (
              <Table.Summary.Row>
                <Table.Summary.Cell index={0}>
                  <strong>Total</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={1}>
                  <Tag color="orange">
                    <strong>{analysis.total_records}</strong>
                  </Tag>
                </Table.Summary.Cell>
              </Table.Summary.Row>
            )}
          />
        )}
      </>
    );
  };

  const renderSummaryStep = () => {
    if (!executionResult) return null;

    return (
      <>
        <Alert
          type="success"
          showIcon
          message="Upgrade Complete"
          description={`Successfully upgraded ${executionResult.total_updated} record(s) in "${
            executionResult.org_name || selectedOrgName
          }".`}
          style={{ marginBottom: 16 }}
        />
        <Descriptions bordered size="small" column={1}>
          <Descriptions.Item label="Organization Status">
            <Tag color="gold">{executionResult.status_set_to || "DRAFT"}</Tag>
          </Descriptions.Item>
        </Descriptions>
        {executionResult.items && executionResult.items.length > 0 && (
          <Table
            dataSource={executionResult.items}
            columns={executionColumns}
            rowKey={(record: UpgradeExecutionItem) => record.table}
            pagination={false}
            size="small"
            style={{ marginTop: 16 }}
            summary={() => (
              <Table.Summary.Row>
                <Table.Summary.Cell index={0}>
                  <strong>Total Updated</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={1}>
                  <Tag color="blue">
                    <strong>{executionResult.total_updated}</strong>
                  </Tag>
                </Table.Summary.Cell>
              </Table.Summary.Row>
            )}
          />
        )}
        <Alert
          type="warning"
          showIcon
          message="Review Required"
          description="The organization has been set to DRAFT status. Please review and republish via the Setup Wizard."
          style={{ marginTop: 16 }}
        />
      </>
    );
  };

  const getTitle = () => {
    switch (step) {
      case "select":
        return "Upgrade Organization - Select";
      case "analysis":
        return "Upgrade Organization - Analysis";
      case "summary":
        return "Upgrade Organization - Summary";
      default:
        return "Upgrade Organization";
    }
  };

  const getFooter = () => {
    switch (step) {
      case "select":
        return [
          <Button key="cancel" onClick={handleClose}>
            Cancel
          </Button>,
          <Button
            key="analyze"
            type="primary"
            onClick={handleAnalyze}
            loading={isAnalyzing}
            disabled={!selectedOrgId}
          >
            Analyze
          </Button>,
        ];
      case "analysis":
        if (analysis?.already_upgraded) {
          return [
            <Button key="close" type="primary" onClick={handleClose}>
              Close
            </Button>,
          ];
        }
        return [
          <Button
            key="back"
            onClick={() => {
              setStep("select");
              setAnalysis(null);
            }}
          >
            Back
          </Button>,
          <Button
            key="execute"
            type="primary"
            onClick={() => setConfirmVisible(true)}
            loading={isExecuting}
            disabled={!analysis || analysis.total_records === 0}
          >
            Execute Upgrade
          </Button>,
        ];
      case "summary":
        return [
          <Button key="close" onClick={handleClose}>
            Close
          </Button>,
          <Button
            key="review"
            type="primary"
            onClick={() => {
              handleClose();
              navigate(`/organization-management/setup-wizard?orgId=${selectedOrgId}`);
            }}
          >
            Review in Setup Wizard
          </Button>,
        ];
      default:
        return [];
    }
  };

  return (
    <>
      <Modal
        title={getTitle()}
        open={open}
        onCancel={handleClose}
        footer={getFooter()}
        width={640}
        destroyOnClose
      >
        <Spin spinning={isAnalyzing || isExecuting}>
          {step === "select" && renderSelectStep()}
          {step === "analysis" && renderAnalysisStep()}
          {step === "summary" && renderSummaryStep()}
        </Spin>
      </Modal>
      <Modal
        title="Confirm Upgrade"
        open={confirmVisible}
        onCancel={() => setConfirmVisible(false)}
        onOk={handleExecute}
        okText="Confirm Upgrade"
        cancelText="Cancel"
        okButtonProps={{ loading: isExecuting }}
      >
        <p>
          This will upgrade <strong>{analysis?.total_records || 0}</strong> records in{" "}
          <strong>{analysis?.org_name || selectedOrgName}</strong> and set the organization to{" "}
          <Tag color="gold">DRAFT</Tag> status.
        </p>
        <p>You will need to review and republish the organization after the upgrade completes.</p>
      </Modal>
    </>
  );
}

export default OrganizationUpgradeModal;
